/**
 * UUID Generator View
 * Interactive UUID generation
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import TextInput from 'ink-text-input';
import { UtilsService } from '../../../services/utils.service.js';

export const UuidView: React.FC = () => {
    const [count, setCount] = useState('1');
    const [uppercase, setUppercase] = useState(false);
    const [noHyphens, setNoHyphens] = useState(false);
    const [uuids, setUuids] = useState<string[]>([]);
    const [focusedField, setFocusedField] = useState<'count' | 'uppercase' | 'hyphens' | 'generate'>('count');
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

    const service = new UtilsService();

    // Auto-generate on first mount
    React.useEffect(() => {
        handleGenerate();
    }, []);

    useInput((input, key) => {
        if (key.tab) {
            const fields: typeof focusedField[] = ['count', 'uppercase', 'hyphens', 'generate'];
            const currentIndex = fields.indexOf(focusedField);
            setFocusedField(fields[(currentIndex + 1) % fields.length]);
        } else if (key.return) {
            if (focusedField === 'uppercase') {
                setUppercase(!uppercase);
            } else if (focusedField === 'hyphens') {
                setNoHyphens(!noHyphens);
            } else {
                // Generate on Enter from any field (except checkboxes)
                handleGenerate();
            }
        } else if (input === 'c' && uuids.length > 0) {
            handleCopy(0);
        }
    });

    const handleGenerate = () => {
        const newUuids: string[] = [];
        const countNum = parseInt(count, 10) || 1;

        for (let i = 0; i < countNum; i++) {
            newUuids.push(service.generateUuid({ uppercase, noHyphens }));
        }
        setUuids(newUuids);
        setCopiedIndex(null);
    };

    const handleCopy = async (index: number) => {
        try {
            const { default: clipboardy } = await import('clipboardy');
            await clipboardy.write(uuids[index]);
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 2000);
        } catch (error) {
            // Clipboard not available
        }
    };

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    🆔 UUID Generator
                </Text>
            </Box>

            <Box
                flexDirection="column"
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                paddingY={1}
                marginBottom={1}
            >
                <Text bold color="yellow" marginBottom={1}>
                    Options:
                </Text>

                <Box marginBottom={1}>
                    <Box width={20}>
                        <Text color={focusedField === 'count' ? 'green' : undefined}>
                            {focusedField === 'count' ? '▶ ' : '  '}Count:
                        </Text>
                    </Box>
                    <Box width={10}>
                        {focusedField === 'count' ? (
                            <TextInput value={count} onChange={setCount} />
                        ) : (
                            <Text>{count}</Text>
                        )}
                    </Box>
                </Box>

                <Box marginBottom={1}>
                    <Text color={focusedField === 'uppercase' ? 'green' : undefined}>
                        {focusedField === 'uppercase' ? '▶ ' : '  '}
                        [{uppercase ? '✓' : ' '}] Uppercase
                    </Text>
                </Box>

                <Box marginBottom={1}>
                    <Text color={focusedField === 'hyphens' ? 'green' : undefined}>
                        {focusedField === 'hyphens' ? '▶ ' : '  '}
                        [{noHyphens ? '✓' : ' '}] No Hyphens
                    </Text>
                </Box>

                <Box marginTop={1}>
                    <Text
                        bold
                        backgroundColor={focusedField === 'generate' ? 'green' : undefined}
                        color={focusedField === 'generate' ? 'black' : 'green'}
                    >
                        {focusedField === 'generate' ? '▶ ' : '  '}
                        [ Generate UUIDs ]
                    </Text>
                </Box>
            </Box>

            {uuids.length > 0 && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="green"
                    paddingX={2}
                    paddingY={1}
                >
                    <Text bold color="green" marginBottom={1}>
                        ✓ Generated UUIDs:
                    </Text>

                    {uuids.map((uuid, index) => (
                        <Box key={index} marginBottom={0}>
                            <Box width={4}>
                                <Text dimColor>{index + 1}.</Text>
                            </Box>
                            <Box flexGrow={1}>
                                <Text>{uuid}</Text>
                            </Box>
                            {copiedIndex === index && (
                                <Text color="green"> ✓ Copied!</Text>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={2}>
                <Text dimColor>
                    Tab: Next | Enter: Toggle/Generate | C: Copy | Esc: Back
                </Text>
            </Box>
        </Box>
    );
};
